import React from 'react';
import { Button } from './Button';
import { Plus, Trash2 } from 'lucide-react';
import { clsx } from 'clsx';

export interface KeyValueRow {
  key: string;
  value: string;
}

interface KeyValueEditorProps {
  label: string;
  rows: KeyValueRow[];
  onChange: (rows: KeyValueRow[]) => void;
  namePlaceholder?: string;
  valuePlaceholder?: string;
  nameHelperText?: string;
  valueHelperText?: string;
  className?: string;
}

const inputClass =
  'w-full min-w-0 rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-3 py-2 text-sm text-app-text dark:text-gray-100';

export const KeyValueEditor: React.FC<KeyValueEditorProps> = ({
  label,
  rows,
  onChange,
  namePlaceholder = 'e.g. key',
  valuePlaceholder = 'e.g. value',
  nameHelperText,
  valueHelperText,
  className,
}) => {
  const addRow = () => {
    onChange([...rows, { key: '', value: '' }]);
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) return;
    onChange(rows.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: 'key' | 'value', value: string) => {
    const next = [...rows];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  return (
    <div className={clsx('min-w-0', className)}>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-app-text dark:text-gray-300">{label}</label>
        <Button type="button" variant="ghost" size="sm" onClick={addRow} aria-label="Add row">
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>
      <div className="space-y-4">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-start">
            <div className="min-w-0">
              <label className="block text-xs font-medium text-app-text-muted dark:text-gray-400 mb-1">
                Name {index + 1}
              </label>
              <input
                type="text"
                value={row.key}
                onChange={(e) => updateRow(index, 'key', e.target.value)}
                className={inputClass}
                placeholder={namePlaceholder}
                spellCheck={false}
              />
              {nameHelperText && (
                <p className="mt-1 text-xs text-app-text-muted dark:text-gray-400">{nameHelperText}</p>
              )}
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-medium text-app-text-muted dark:text-gray-400 mb-1">
                Value {index + 1}
              </label>
              <input
                type="text"
                value={row.value}
                onChange={(e) => updateRow(index, 'value', e.target.value)}
                className={inputClass}
                placeholder={valuePlaceholder}
                spellCheck={false}
              />
              {valueHelperText && (
                <p className="mt-1 text-xs text-app-text-muted dark:text-gray-400">{valueHelperText}</p>
              )}
            </div>
            <div className="flex items-end h-[74px] sm:h-[42px]">
              <button
                type="button"
                onClick={() => removeRow(index)}
                disabled={rows.length <= 1}
                className="p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:text-error-500 hover:bg-app-surface-muted dark:hover:bg-gray-700 disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Remove row"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
